import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock3 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NewsletterCard from "@/components/newsletter/NewsletterCard";
import { newsletterApi } from "@/lib/newsletterApi";

const renderParagraphs = (content) =>
  String(content || "")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [relatedCaseStudies, setRelatedCaseStudies] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const handleContactClick = () => {
    window.location.assign("/contact");
  };

  useEffect(() => {
    const loadCaseStudy = async () => {
      try {
        const data = await newsletterApi.getCaseStudy(slug);
        setCaseStudy(data.caseStudy);
        setRelatedCaseStudies(data.relatedCaseStudies || []);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "Unable to load this case study.");
      }
    };

    loadCaseStudy();
  }, [slug]);

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onContactClick={() => window.location.assign("/")} />
        <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center px-4 pt-32 text-center">
          <div>
            <p className="text-lg text-gray-600">{errorMessage}</p>
            <Link to="/case-studies" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-purple-700">
              <ArrowLeft className="h-4 w-4" />
              Back to case studies
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onContactClick={() => window.location.assign("/")} />
        <div className="mx-auto max-w-4xl px-4 pt-32 text-center text-gray-600">Loading case study...</div>
      </div>
    );
  }

  const paragraphs = renderParagraphs(caseStudy.content);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onContactClick={() => window.location.assign("/")} />

      <section className="section-gradient-light pt-24">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-bold text-purple-700">
            <ArrowLeft className="h-4 w-4" />
            Back to all case studies
          </Link>

          <div className="mt-5 max-w-4xl">
            <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded-full bg-purple-50 px-3 py-1 font-semibold text-purple-700">
                {caseStudy.category}
              </span>
              <span className="text-gray-500">{new Date(caseStudy.publishedAt).toLocaleDateString()}</span>
              <span className="inline-flex items-center gap-1 text-gray-500">
                <Clock3 className="h-4 w-4" />
                {caseStudy.readingTime} min read
              </span>
            </div>
            <h1 className="font-heading text-3xl font-black leading-tight text-gray-900 sm:text-5xl">
              {caseStudy.title}
            </h1>
            <p className="mt-4 text-base leading-7 text-gray-600 sm:text-lg">{caseStudy.excerpt}</p>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {caseStudy.coverImage ? (
          <img
            src={caseStudy.coverImage}
            alt={caseStudy.title}
            className="h-[300px] w-full rounded-[24px] object-cover shadow-[0_18px_50px_rgba(123,31,162,0.12)] sm:h-[360px]"
          />
        ) : (
          <div className="flex h-[300px] w-full items-end rounded-[24px] bg-[linear-gradient(135deg,#f8dbff_0%,#efe4ff_45%,#d9d7ff_100%)] p-6 shadow-[0_18px_50px_rgba(123,31,162,0.12)] sm:h-[360px]">
            <div className="max-w-xl rounded-[22px] bg-white/70 p-6 backdrop-blur">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-purple-700">{caseStudy.category}</div>
              <div className="mt-3 font-heading text-2xl font-black text-gray-900 sm:text-3xl">{caseStudy.title}</div>
            </div>
          </div>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <article className="newsletter-prose rounded-[24px] border border-purple-100 bg-white p-5 shadow-[0_18px_50px_rgba(123,31,162,0.08)] sm:p-7">
            {paragraphs.map((paragraph, index) => (
              <motion.p key={`${caseStudy._id}-${index}`} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                {paragraph}
              </motion.p>
            ))}
          </article>

          <aside className="space-y-4">
            <button
              type="button"
              onClick={handleContactClick}
              className="primary-btn w-full rounded-[22px] px-5 py-4 text-sm font-bold text-white"
            >
              Book Consultation
            </button>

            <div className="rounded-[22px] bg-gray-900 p-5 text-white">
              <div className="text-[11px] uppercase tracking-[0.3em] text-purple-200">Author</div>
              <h2 className="mt-2 font-heading text-xl font-bold">{caseStudy.authorName}</h2>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                Detailed outcomes, execution context, and the strategic lessons behind the result.
              </p>
            </div>

            {!!caseStudy.highlights?.length && (
              <div className="rounded-[22px] border border-purple-100 bg-purple-50/70 p-5">
                <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Key takeaways</div>
                <div className="mt-4 space-y-2.5">
                  {caseStudy.highlights.map((item) => (
                    <div key={item} className="rounded-xl bg-white px-3.5 py-3 text-sm font-medium leading-6 text-gray-700 shadow-sm">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!caseStudy.tags?.length && (
              <div className="rounded-[22px] border border-purple-100 bg-white p-5">
                <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Tags</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {caseStudy.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-purple-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {!!relatedCaseStudies.length && (
          <section className="mt-12">
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Related reads</div>
              <h2 className="mt-2 font-heading text-3xl font-black text-gray-900">More case studies</h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              {relatedCaseStudies.map((item) => (
                <NewsletterCard key={item._id} article={item} linkBase="/case-studies" />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer onContactClick={() => window.location.assign("/")} />
    </div>
  );
};

export default CaseStudyDetailPage;
